import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Loader2, Save, Rss } from 'lucide-react';
import { toast } from 'sonner';
import {
  useWebTrendSources,
  useUpsertWebTrendSource,
  useDeleteWebTrendSource,
  useWebTrendsSearchPrompt,
  useUpdateWebTrendsSearchPrompt,
} from '@/hooks/useWebTrends';

const DEFAULT_PROMPT = `Eres un curador de noticias de Inteligencia Artificial. Devuelve entre 8 y 15 noticias muy recientes (últimos 7 días si es posible) sobre tendencias de IA aplicada a empresas: agentes IA, LLMs, casos de uso empresariales, regulación, herramientas nuevas, lanzamientos relevantes y novedades de los principales laboratorios. Para cada noticia incluye: title, summary (2-3 frases), url (enlace original real, no inventes), source_name (medio), published_at (ISO date si la conoces). Prioriza fuentes en español o inglés de portales reconocidos.`;

export default function WebTrendsConfigTab() {
  const { data: sources = [], isLoading } = useWebTrendSources();
  const upsert = useUpsertWebTrendSource();
  const del = useDeleteWebTrendSource();
  const { data: promptData } = useWebTrendsSearchPrompt();
  const updatePrompt = useUpdateWebTrendsSearchPrompt();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [prompt, setPrompt] = useState('');

  useEffect(() => {
    if (promptData) setPrompt(promptData.prompt || '');
  }, [promptData]);

  const handleAdd = async () => {
    if (!name.trim() || !url.trim()) {
      toast.error('Nombre y URL requeridos');
      return;
    }
    try {
      await upsert.mutateAsync({ name: name.trim(), url: url.trim(), enabled: true });
      setName(''); setUrl('');
      toast.success('Fuente añadida');
    } catch (e: any) { toast.error(e?.message ?? 'Error'); }
  };

  const handleToggle = async (id: string, enabled: boolean, name: string, url: string) => {
    await upsert.mutateAsync({ id, name, url, enabled });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar esta fuente?')) return;
    await del.mutateAsync(id);
    toast.success('Eliminada');
  };

  const handleSavePrompt = async () => {
    try {
      await updatePrompt.mutateAsync(prompt);
      toast.success('Prompt guardado');
    } catch (e: any) { toast.error(e?.message ?? 'Error'); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Rss className="h-5 w-5" />Fuentes RSS de IA</CardTitle>
          <CardDescription>Portales que se consultan al pulsar "Actualizar" en Tendencias de la Web.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end">
            <div>
              <Label>Nombre</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: TechCrunch AI" />
            </div>
            <div>
              <Label>URL del feed RSS</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
            </div>
            <Button onClick={handleAdd} disabled={upsert.isPending}>
              <Plus className="mr-2 h-4 w-4" />Añadir
            </Button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead className="w-[120px]">Última actualización</TableHead>
                  <TableHead className="w-[80px]">Activa</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Sin fuentes configuradas</TableCell></TableRow>
                ) : sources.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-xs">{s.url}</TableCell>
                    <TableCell className="text-xs">{s.last_fetched_at ? new Date(s.last_fetched_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>
                      <Switch checked={s.enabled} onCheckedChange={(v) => handleToggle(s.id, v, s.name, s.url)} />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Prompt de búsqueda IA</CardTitle>
          <CardDescription>
            Instrucción para Gemini al buscar noticias en internet. Si se deja vacío se usa el prompt por defecto.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={DEFAULT_PROMPT}
            rows={10}
            className="font-mono text-xs"
          />
          <div className="flex gap-2">
            <Button onClick={handleSavePrompt} disabled={updatePrompt.isPending}>
              <Save className="mr-2 h-4 w-4" />Guardar prompt
            </Button>
            <Button variant="outline" onClick={() => setPrompt(DEFAULT_PROMPT)}>Restaurar por defecto</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
